import Alpine from 'alpinejs'
import type { IEstablishment, IForm, IFormField, IVehicle } from './types/datamotive-form-types'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import 'flatpickr/dist/themes/material_green.css'

export const initDatamotiveForms = (): void => {
    Alpine.store('datamotiveForm', {
        currentFormName: '',
    })

    Alpine.data('initDatamotiveForms', (optionalFormId?: number) => ({
        forms: [] as IForm[],
        currentFormId: (optionalFormId ?? null) as number | null,
        isInline: Boolean(optionalFormId) as boolean,
        currentVehicle: {} as IVehicle,
        licensePlate: '' as string,
        hasLicensePlateError: false as boolean,
        currentStep: 0 as number,
        filePreviews: {} as Record<string, Array<{ file: File; preview: string }>>,
        fileUploadErrors: {} as Record<string, string>,
        originalHtml: '' as string,
        isSuccessPageShown: false as boolean,
        isLoading: false as boolean,
        establishments: [] as IEstablishment[],
        selectedEstablishment: {} as IEstablishment,
        formInvalid: false as boolean,
        formErrorMessage: '' as string,
        carDependencyData: {} as Record<string, unknown>,
        pendingHiddenFieldValues: {} as Record<string, string>,
        // Helper functions
        getFieldElement(fieldName: string): HTMLElement | null {
            return this.$root.querySelector(`[name="${fieldName}"]`)
        },
        getInputElement(fieldName: string): HTMLInputElement | null {
            return this.$root.querySelector(`input[name="${fieldName}"]`) as HTMLInputElement
        },
        getNestedInputElement(parentFieldName: string, childFieldName: string): HTMLInputElement | null {
            return this.$root.querySelector(`input[name="${parentFieldName}.${childFieldName}"]`) as HTMLInputElement
        },
        setFieldInvalid(fieldName: string, isValid: boolean) {
            const fieldElement = this.getFieldElement(fieldName)
            if (fieldElement) {
                if (isValid) {
                    fieldElement.classList.remove('form__invalid')
                } else {
                    fieldElement.classList.add('form__invalid')
                }
            }
        },
        // Surrounding tools (finance tool, private lease calculator) push their configuration in by field label.
        // Stashed as well, so a fill that lands before the form definition arrives is replayed once it does.
        applyExternalData(values: Record<string, string>) {
            this.carDependencyData = { ...this.carDependencyData, ...values }
            this.pendingHiddenFieldValues = { ...this.pendingHiddenFieldValues, ...values }
            this.fillHiddenFields(values)
        },
        // Hidden fields have generated names, so incoming values are matched on their label.
        fillHiddenFields(values: Record<string, string>) {
            for (const form of this.forms) {
                for (const step of form.steps) {
                    for (const field of step.fields) {
                        if (field.type === 'hidden' && values[field.label] !== undefined) {
                            field.value = values[field.label]
                        }
                    }
                }
            }
        },
        findFieldByType(type: string): IFormField | null {
            for (const form of this.forms) {
                for (const step of form.steps) {
                    const field = step.fields.find((f) => f.type === type)
                    if (field) {
                        return field
                    }
                }
            }
            return null
        },
        extractFormDataValues(formData: FormData): Record<string, string> {
            const formDataValues: Record<string, string> = {}
            for (const [key, value] of formData.entries()) {
                if (!key.startsWith('ignorefile_') && value !== '' && value !== null && typeof value !== 'undefined') {
                    formDataValues[key] = value.toString()
                }
            }
            return formDataValues
        },
        hasValue(formData: FormData, formDataKeys: string[], fieldName: string, formDataValues: Record<string, string>): boolean {
            const fieldValue = formDataValues[fieldName]
            if (fieldValue !== undefined && fieldValue !== '' && fieldValue !== null) {
                return true
            }
            for (const key of formDataKeys) {
                if (key.startsWith(`${fieldName}.`)) {
                    const nestedValue = formData.get(key)
                    if (nestedValue !== null && nestedValue !== '' && nestedValue !== undefined) {
                        return true
                    }
                }
            }
            return false
        },
        initializeGroupedData(groupedData: Record<string, Record<string, string> | string>, parentFieldName: string) {
            if (!groupedData[parentFieldName] || typeof groupedData[parentFieldName] === 'string') {
                groupedData[parentFieldName] = {}
            }
        },
        extractSuccessPageContent(data: any, currentForm: IForm | null): string | null {
            if (data?.successPage) {
                return typeof data.successPage === 'string' ? data.successPage : data.successPage?.text || data.successPage
            }
            if (currentForm?.successPage) {
                return typeof currentForm.successPage === 'string'
                    ? currentForm.successPage
                    : (currentForm.successPage as any)?.text || currentForm.successPage
            }
            return null
        },
        cleanupFormData(formData: FormData) {
            for (const [key, value] of Array.from(formData.entries())) {
                if (
                    value === '' ||
                    key.startsWith('ignorefile_') ||
                    value === null ||
                    typeof value === 'undefined' ||
                    (value instanceof File && value.size === 0 && value.name === '')
                ) {
                    formData.delete(key)
                }
            }
        },
        initFlatpickr(element: HTMLInputElement, activeDays: string[]) {
            const dayIndexByName: Record<string, number> = {
                sunday: 0,
                monday: 1,
                tuesday: 2,
                wednesday: 3,
                thursday: 4,
                friday: 5,
                saturday: 6,
            }
            const activeDayIndices = Array.isArray(activeDays)
                ? activeDays.map((day) => dayIndexByName[day.toLowerCase()]).filter((index) => index !== undefined)
                : []

            flatpickr(element, {
                appendTo: this.$root,
                dateFormat: 'Y-m-d',
                disable:
                    activeDayIndices.length > 0
                        ? [
                              function (date) {
                                  return !activeDayIndices.includes(date.getDay())
                              },
                          ]
                        : [],
                locale: {
                    firstDayOfWeek: 1, // start week on Monday
                },
                position(instance) {
                    const calendar = instance.calendarContainer
                    const inputBounds = instance._positionElement.getBoundingClientRect()
                    const calendarHeight = Array.from(calendar.children).reduce((height, child) => height + (child as HTMLElement).offsetHeight, 0)
                    const showOnTop = window.innerHeight - inputBounds.bottom < calendarHeight && inputBounds.top > calendarHeight

                    calendar.classList.toggle('arrowTop', !showOnTop)
                    calendar.classList.toggle('arrowBottom', showOnTop)
                    calendar.style.position = 'fixed'
                    calendar.style.top = `${showOnTop ? inputBounds.top - calendarHeight - 2 : inputBounds.bottom + 2}px`
                    calendar.style.left = `${inputBounds.left}px`
                    calendar.style.right = 'auto'
                },
            })
        },
        async init() {
            if (this.$root) {
                this.originalHtml = this.$root.innerHTML
            }

            // Registered by every instance; only the one holding this form id submits.
            window.addEventListener('submit-private-lease-calculator', async (event: Event) => {
                const detail = (event as CustomEvent<{ formId: number; dependencyData?: Record<string, string> }>).detail
                if (this.currentFormId !== Number(detail?.formId)) {
                    return
                }
                // The chosen lease configuration travels along as dependencyData and fills the hidden fields.
                if (detail.dependencyData) {
                    this.applyExternalData(detail.dependencyData)
                    // The hidden inputs are bound to field.value, so let Alpine write them before reading the form.
                    await this.$nextTick()
                }
                this.submitForm()
            })

            // Same payload, but the form keeps its own submit button — the values are pushed in beforehand.
            window.addEventListener('fill-datamotive-form-data', (event: Event) => {
                const detail = (event as CustomEvent<{ formId: number; dependencyData?: Record<string, string> }>).detail
                if (this.currentFormId !== Number(detail?.formId) || !detail.dependencyData) {
                    return
                }
                this.applyExternalData(detail.dependencyData)
            })

            // After a submit the form holds its success page, so rebuild it from scratch when the owner reopens.
            window.addEventListener('reset-datamotive-form', async (event: Event) => {
                const detail = (event as CustomEvent<{ formId: number }>).detail
                if (this.currentFormId !== Number(detail?.formId)) {
                    return
                }
                const formId = this.currentFormId
                this.resetForm()
                this.currentFormId = formId
                await this.getFormData(formId)
            })

            if (this.isInline && this.currentFormId) {
                await this.getFormData(this.currentFormId)
                return
            }

            window.addEventListener('open-datamotive-form-dialog', async (event: Event) => {
                const customEvent = event as CustomEvent<{ formId: number; car?: Record<string, unknown> }>
                const formId = customEvent.detail.formId

                this.resetForm()

                if (customEvent.detail.car && typeof customEvent.detail.car === 'object') {
                    this.carDependencyData = customEvent.detail.car
                }

                this.currentFormId = formId
                await this.getFormData(formId)
            })
        },
        resetForm() {
            this.forms = []
            this.establishments = []
            this.formInvalid = false
            this.formErrorMessage = ''
            this.selectedEstablishment = {}
            this.currentStep = 0
            this.currentVehicle = {}
            this.licensePlate = ''
            this.filePreviews = {}
            this.fileUploadErrors = {}
            this.carDependencyData = {}
            this.pendingHiddenFieldValues = {}

            if (this.isSuccessPageShown && this.$root && this.originalHtml) {
                this.$root.innerHTML = this.originalHtml
                this.isSuccessPageShown = false
            }
        },
        async getFormData(formId: number) {
            try {
                this.currentStep = 0
                const response = await fetch(`https://api.datamotive.nl/api/v1/form/${formId}`)
                const data = await response.json()

                const rawForms = Array.isArray(data) ? data : [data]

                this.forms = rawForms.map((form: any) => ({
                    name: form.name || '',
                    buttonText: form.submitButtonText || '',
                    steps: (form.steps || []).map((step: any) => ({
                        name: step.name || '',
                        submitButtonText: step.submitButtonText || '',
                        fields: (step.fields || []).map((field: any) => ({
                            name: field.name || '',
                            type: field.type || '',
                            value: field.props?.value || '',
                            label: field.props?.label || '',
                            placeholder: field.props?.placeholder || '',
                            allowedFileTypes: field.props?.allowedFileTypes || false,
                            uploads: Array.isArray(field.props?.uploads) ? field.props.uploads : [],
                            choices: Array.isArray(field.props?.choices) ? field.props.choices : [],
                            variant: field.props?.variant || '',
                            required: field.required || false,
                            colSize: Number(field.props?.colSize) || 12,
                            activeDays: Array.isArray(field.props?.days) ? field.props.days : [],
                        })),
                    })),
                    successPage: form.successPage || '',
                }))

                this.forms.forEach((form) => {
                    form.steps.forEach((step) => {
                        step.fields.forEach(async (field) => {
                            if (field.type === 'establishment') {
                                this.isLoading = true
                                await fetch('/actions/_datamotive/establishment/get-establishments')
                                    .then((response) => response.json())
                                    .then(async (data) => {
                                        this.establishments = data.establishments

                                        await this.$nextTick()

                                        const firstLocationField = this.$root.querySelector(`#${field.name}_0`)
                                        firstLocationField?.classList.add('form-establishments__selected')
                                        const input = this.getInputElement(field.name)
                                        if (data.establishments.length > 0 && input) {
                                            input.value = data.establishments[0].id as string
                                            this.selectedEstablishment = data.establishments[0]
                                        }

                                        this.isLoading = false
                                    })
                                    .catch(() => {
                                        this.isLoading = false
                                    })
                            }
                        })
                    })
                })

                // Values pushed in before this fetch resolved had no fields to land on yet.
                if (Object.keys(this.pendingHiddenFieldValues).length > 0) {
                    this.fillHiddenFields(this.pendingHiddenFieldValues)
                }

                // The store only drives the dialog header, so inline forms must not overwrite it.
                if (!this.isInline && this.forms.length > 0 && this.forms[0].name) {
                    ;(Alpine.store('datamotiveForm') as { currentFormName: string }).currentFormName = this.forms[0].name
                }

                return this.forms
            } catch {
                this.forms = []
                this.establishments = []
                if (!this.isInline) {
                    ;(Alpine.store('datamotiveForm') as { currentFormName: string }).currentFormName = ''
                }
            }
        },
        generateFilePreviews(parentFieldName: string, uploadName: string, files: FileList | null): Promise<void> {
            return new Promise((resolve) => {
                if (!files || files.length === 0) {
                    resolve()
                    return
                }

                const previewKey = `${parentFieldName}_${uploadName}`
                const fileArray = Array.from(files)
                const previews: Array<{ file: File; preview: string }> = []
                let loadedCount = 0

                fileArray.forEach((file) => {
                    if (file.type.startsWith('image/')) {
                        const reader = new FileReader()
                        reader.onload = (e) => {
                            previews.push({
                                file,
                                preview: e.target?.result as string,
                            })
                            loadedCount++
                            if (loadedCount === fileArray.length) {
                                this.filePreviews[previewKey] = previews
                                resolve()
                            }
                        }
                        reader.onerror = () => {
                            loadedCount++
                            if (loadedCount === fileArray.length) {
                                this.filePreviews[previewKey] = previews
                                resolve()
                            }
                        }
                        reader.readAsDataURL(file)
                    } else {
                        previews.push({
                            file,
                            preview: '',
                        })
                        loadedCount++
                        if (loadedCount === fileArray.length) {
                            this.filePreviews[previewKey] = previews
                            resolve()
                        }
                    }
                })

                if (fileArray.length === 0) {
                    resolve()
                }
            })
        },
        removePreview(parentFieldName: string, uploadName: string, index: number) {
            const previewKey = `${parentFieldName}_${uploadName}`
            if (this.filePreviews[previewKey]) {
                this.filePreviews[previewKey].splice(index, 1)
                if (this.filePreviews[previewKey].length === 0) {
                    delete this.filePreviews[previewKey]
                }
            }

            // Clear any error for this field
            if (this.fileUploadErrors[previewKey]) {
                delete this.fileUploadErrors[previewKey]
            }

            const inputName = `ignorefile_${parentFieldName}.${uploadName}`
            const fileInput = this.getInputElement(inputName)
            const hiddenFileInput = this.getNestedInputElement(parentFieldName, uploadName)
            if (fileInput) {
                fileInput.value = ''
            }
            if (hiddenFileInput) {
                hiddenFileInput.value = ''
            }
        },
        async uploadFile(parentFieldName: string, uploadName: string, files: FileList | null) {
            if (!files || files.length === 0) {
                return
            }

            const previewKey = `${parentFieldName}_${uploadName}`
            const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes

            // Check file sizes before uploading
            for (const file of Array.from(files)) {
                if (file.size > MAX_FILE_SIZE) {
                    // Set error message
                    this.fileUploadErrors[previewKey] = `Bestand "${file.name}" is te groot. Maximale bestandsgrootte is 10MB.`

                    // Clear the file input
                    const inputName = `ignorefile_${parentFieldName}.${uploadName}`
                    const fileInput = this.getInputElement(inputName)
                    if (fileInput) {
                        fileInput.value = ''
                    }

                    // Clear any existing previews for this field
                    if (this.filePreviews[previewKey]) {
                        delete this.filePreviews[previewKey]
                    }

                    return
                }
            }

            // Clear any previous error for this field
            if (this.fileUploadErrors[previewKey]) {
                delete this.fileUploadErrors[previewKey]
            }

            await this.generateFilePreviews(parentFieldName, uploadName, files)

            const formData = new FormData()

            Array.from(files).forEach((file) => {
                formData.append('file', file)
            })

            const response = await fetch(`https://api.datamotive.nl/api/v1/form/${this.currentFormId}/file`, {
                method: 'POST',
                body: formData,
            })

            if (!response.ok) {
                const errorText = await response.text()
                throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`)
            }

            const data = await response.json()
            const input = this.getNestedInputElement(parentFieldName, uploadName)
            if (input) {
                input.value = data.id
            }
            return data
        },
        async licensePlateCheck(licensePlate: string) {
            const normalizedPlate = licensePlate.includes('-') ? licensePlate.trim().replace(/-/g, '') : licensePlate
            if (normalizedPlate.length !== 6) {
                this.currentVehicle = {}
                this.hasLicensePlateError = normalizedPlate.length > 6
                return
            }
            this.currentVehicle = {}
            this.isLoading = true
            try {
                const response = await fetch(`https://api.datamotive.nl/api/v1/form/${this.currentFormId}/vehicle?licensePlate=${normalizedPlate}`, {
                    method: 'GET',
                })
                const data = await response.json()

                if (data.code !== undefined && data.code === 400) {
                    this.currentVehicle = {}
                    this.hasLicensePlateError = true
                    this.isLoading = false
                    return
                }
                if (data.licensePlate) {
                    this.currentVehicle = data
                    this.isLoading = false
                    this.hasLicensePlateError = false
                }
                this.isLoading = false
            } catch (error) {
                console.error(error)
                this.isLoading = false
            }
        },
        async submitForm() {
            this.validateForm()
            if (!this.validateForm()) {
                return
            }
            const formElement = this.$root as HTMLFormElement
            if (!formElement) {
                return
            }
            const formData = new FormData(formElement)
            this.cleanupFormData(formData)

            const groupedData: Record<string, Record<string, string> | string> = {}
            const flatData: Record<string, string> = {}

            for (const [key, value] of formData.entries()) {
                // Handle bracket notation like dependencyData[establishment]
                const bracketMatch = key.match(/^([^\[]+)\[([^\]]+)\]$/)
                if (bracketMatch) {
                    const [, parentFieldName, childFieldName] = bracketMatch
                    this.initializeGroupedData(groupedData, parentFieldName)
                    const parentData = groupedData[parentFieldName] as Record<string, string>
                    parentData[childFieldName] = value.toString()
                }
                // Handle dot notation like field.upload
                else if (key.includes('.')) {
                    const [parentFieldName, uploadName] = key.split('.', 2)
                    this.initializeGroupedData(groupedData, parentFieldName)
                    const parentData = groupedData[parentFieldName] as Record<string, string>
                    parentData[uploadName] = value.toString()
                } else {
                    flatData[key] = value.toString()
                }
            }

            const combinedData: Record<string, unknown> = {
                ...flatData,
                ...groupedData,
            }

            // Add car details from the VDP page (passed via the open-form event) as dependencyData,
            // keeping any existing dependencyData (e.g. establishment) from the form's hidden inputs.
            if (Object.keys(this.carDependencyData).length > 0) {
                const existingDependencyData = (combinedData.dependencyData as Record<string, unknown>) || {}
                combinedData.dependencyData = {
                    ...this.carDependencyData,
                    ...existingDependencyData,
                }
            }

            if (Object.keys(this.currentVehicle).length > 0) {
                const licensePlateField = this.findFieldByType('licensePlate')
                if (licensePlateField) {
                    combinedData[licensePlateField.name] = { ...this.currentVehicle }
                }

                if (this.licensePlate) {
                    combinedData.licensePlate = this.licensePlate
                }
                if (this.currentVehicle.brand) {
                    combinedData.brand = this.currentVehicle.brand
                }
                if (this.currentVehicle.model) {
                    combinedData.model = this.currentVehicle.model
                }
            }

            const response = await fetch(`https://api.datamotive.nl/api/v1/form/${this.currentFormId}/submit`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Dm-Referrer': window.location.href,
                },
                body: JSON.stringify(combinedData),
            })
            const data = await response.json()

            // Check for 400 error code - don't submit and show error
            if (data.code !== undefined && data.code === 400) {
                this.formInvalid = true

                let validationMessage = ''
                if (typeof data.message === 'string') {
                    validationMessage = data.message
                } else if (data.message && typeof data.message === 'object') {
                    validationMessage = Object.values(data.message as Record<string, unknown>)
                        .filter((value): value is string => typeof value === 'string')
                        .join(' ')
                }

                this.formErrorMessage = validationMessage || 'Er is iets fout gegaan. Probeer het later opnieuw of neem contact met ons op.'
                return
            }

            const currentForm = this.forms.length > 0 ? this.forms[0] : null
            const successPageContent = this.extractSuccessPageContent(data, currentForm)

            if (successPageContent) {
                formElement.innerHTML = successPageContent
                this.isSuccessPageShown = true
                // Lets surrounding components (e.g. the finance tool) hide their own controls.
                // Both the element and the event target are the form captured above, never $el/$root: when submit
                // came from the button, that button is detached by the swap and no longer resolves a root to bubble to.
                formElement.dispatchEvent(new CustomEvent('datamotive-form-success', { detail: { formId: this.currentFormId }, bubbles: true }))
            }
        },
        nextStep() {
            if (this.validateForm()) {
                this.currentStep++
            }
        },
        validateForm(): boolean {
            // Reset custom error message to use default validation message
            this.formErrorMessage = ''

            const formElement = this.$root as HTMLFormElement
            if (!formElement) {
                this.formInvalid = true
                return false
            }

            const formData = new FormData(formElement)
            const formDataKeys = Array.from(formData.keys())
            const formDataValues = this.extractFormDataValues(formData)

            // Check if there are any upload fields in the current step and if at least one file is uploaded
            let hasUploadFields = false
            let hasAtLeastOneFile = false

            // First, check if current step has upload fields
            for (const form of this.forms) {
                const step = form.steps[this.currentStep]
                if (!step) {
                    continue
                }

                for (const field of step.fields) {
                    if (field.uploads && Array.isArray(field.uploads) && field.uploads.length > 0) {
                        hasUploadFields = true
                        break
                    }
                }

                if (hasUploadFields) {
                    break
                }
            }

            // If there are upload fields, check if at least one file is uploaded (check all steps)
            if (hasUploadFields) {
                // Check filePreviews for any uploaded files
                for (const previewKey in this.filePreviews) {
                    if (this.filePreviews[previewKey] && this.filePreviews[previewKey].length > 0) {
                        hasAtLeastOneFile = true
                        break
                    }
                }

                // Also check formData for uploaded file IDs (check all fields with dot notation)
                if (!hasAtLeastOneFile) {
                    for (const key of formDataKeys) {
                        if (key.includes('.') && !key.startsWith('ignorefile_')) {
                            const value = formData.get(key)
                            if (value && value.toString().trim() !== '') {
                                hasAtLeastOneFile = true
                                break
                            }
                        }
                    }
                }
            }

            // If there are upload fields but no files uploaded, mark as invalid
            if (hasUploadFields && !hasAtLeastOneFile) {
                // Mark all upload fields as invalid
                for (const form of this.forms) {
                    const step = form.steps[this.currentStep]
                    if (!step) {
                        continue
                    }

                    for (const field of step.fields) {
                        if (field.uploads && Array.isArray(field.uploads) && field.uploads.length > 0) {
                            for (const upload of field.uploads) {
                                // Handle both object format (upload.name) and string format
                                const uploadName = typeof upload === 'string' ? upload : upload.name
                                if (!uploadName) continue

                                const uploadInput = this.getNestedInputElement(field.name, uploadName)
                                const fileInput = this.getInputElement(`ignorefile_${field.name}.${uploadName}`)

                                if (uploadInput) {
                                    uploadInput.classList.add('form__invalid')
                                }
                                if (fileInput) {
                                    fileInput.classList.add('form__invalid')
                                }
                            }
                        }
                    }
                }
            }

            let isValid = true

            for (const form of this.forms) {
                const step = form.steps[this.currentStep]
                if (!step) {
                    this.formInvalid = true
                    return false
                }

                for (const field of step.fields) {
                    this.setFieldInvalid(field.name, true)
                }

                for (const field of step.fields) {
                    if (field.required) {
                        const hasValue = this.hasValue(formData, formDataKeys, field.name, formDataValues)
                        if (!hasValue) {
                            this.setFieldInvalid(field.name, false)
                            isValid = false
                        } else {
                            this.setFieldInvalid(field.name, true)
                        }
                    }
                }

                // Validate license plate check if licensePlate field exists
                const licensePlateField = step.fields.find((field) => field.type === 'licensePlate')
                if (licensePlateField) {
                    const licensePlateValue = formDataValues[licensePlateField.name]
                    const hasLicensePlateValue = licensePlateValue !== undefined && licensePlateValue !== '' && licensePlateValue !== null

                    if (hasLicensePlateValue && !this.currentVehicle.licensePlate) {
                        this.setFieldInvalid(licensePlateField.name, false)
                        isValid = false
                    }
                }
            }

            // If there are upload fields but no files uploaded, form is invalid
            if (hasUploadFields && !hasAtLeastOneFile) {
                isValid = false
                this.formInvalid = true
            }
            this.formInvalid = !isValid

            return isValid
        },
        constructFormLabel(field: IFormField): string {
            if (!field) {
                return ''
            }
            return field.required ? `${field.label} *` : field.label
        },
        selectEstablishment(establishmentId: string, fieldName: string) {
            const input = this.getInputElement(fieldName)
            if (input) {
                input.value = establishmentId
            }

            // Also update the dependencyData[establishment] hidden input if it exists
            const dependencyInput = this.getInputElement('dependencyData[establishment]')
            if (dependencyInput) {
                dependencyInput.value = establishmentId
            }

            // Find and store the selected establishment object
            // Convert to number for comparison since est.id is a number but select value is a string
            const idToFind = Number(establishmentId)
            const establishment = this.establishments.find((est) => est.id === idToFind || String(est.id) === establishmentId)
            if (establishment) {
                this.selectedEstablishment = establishment
            } else {
                this.selectedEstablishment = {}
            }
        },
        getSubmitButtonText() {
            return this.forms[0].steps[0].submitButtonText
        },
    }))
}
